import { usePopover } from '@molecules/BEOffScreen';
import ScreenSize from '../../constants/screen-sizes';
import { useCallback, useEffect, useState } from 'react';
import BEIcon from '../../molecules/BEIcon';
import DateField from './DateField';
import UUID from '@business/utils/uuid';
import { useMemo } from 'react';
import CalendarPopover from './CalendarPopover';
import DatePickerProps from './DatePickerProps';
import DatePickerModel from '@data/models/DatePicker';
import { useMediaQuery } from '@chakra-ui/react';
import { StyledDatePicker } from './StyledBEDatePicker';

const DatePicker = ({
  checkin,
  checkout,
  size = 'lg',
  calendarIcon: showCalendarIcon = true,
  border = true,
  parentRef,
  onChangeDates,
  onClosePopover,
  isFetchingAccomodations,
  defaults,
  checkinPlaceholder,
  checkoutPlaceholder,
}: DatePickerProps) => {
  const [containerRef, setContainerRef] = useState<any>();
  const pickerId = useMemo(() => UUID(), []);

  const [dates, setDates] = useState<DatePickerModel>({
    checkin,
    checkout,
  });

  const [tag, setCurrentTag] = useState<'checkin' | 'checkout' | undefined>(
    undefined
  );

  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);
  const datefieldSize = useMemo(() => {
    if (isMobile && size === 'lg') return 'md';
    return size;
  }, [isMobile, size]);

  const beforeClosePopover = useCallback(() => {
    return true;
  }, []);

  const popover = usePopover(CalendarPopover, {
    id: pickerId,
    referenceElement: parentRef || containerRef,
    renderMethod: isMobile ? 'fullscreen' : 'reference',
    beforeClose: beforeClosePopover,
    ...(isMobile && { pinTo: 'bottom', hasOverlay: true }),
  });

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  useEffect(() => {
    if (isFetchingAccomodations) {
      setCurrentTag(undefined);
      if (isPopoverOpen) {
        setIsPopoverOpen(false);
        popover.close();
      }
    }
  }, [isFetchingAccomodations, isPopoverOpen, popover]);

  useEffect(() => {
    const open = popover.isOpened();
    if (!open && open !== isPopoverOpen) {
      if (defaults && !dates.checkin && !dates.checkout) {
        setDates(defaults);
        setCurrentTag(undefined);
      }
      onClosePopover?.();
      setIsPopoverOpen(false);
    }
  }, [
    dates.checkin,
    dates.checkout,
    defaults,
    isPopoverOpen,
    onClosePopover,
    popover,
  ]);

  const onSelectDate = useCallback(
    async (value: DatePickerModel, tag: 'checkin' | 'checkout') => {
      setDates(value);
      onChangeDates?.(value);

      if (tag === 'checkout' && value.checkin && value.checkout) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        if (!isMobile) {
          popover.close();
        }
      } else if (value.checkin) {
        setCurrentTag('checkout');
        setIsPopoverOpen(true);
        popover.open({
          tag: 'checkout',
          ...dates,
          onSelectDate,
        });
      } else {
        setCurrentTag('checkin');
        setIsPopoverOpen(true);
        popover.open({
          tag: 'checkin',
          ...dates,
          onSelectDate,
        });
      }
    },
    [dates, isMobile, onChangeDates, popover]
  );

  const openWithTag = useCallback(
    (step: 'checkin' | 'checkout' | undefined) => {
      const _dates = { ...dates };
      if (tag === undefined) {
        _dates.checkin = undefined;
        _dates.checkout = undefined;
        setDates(_dates);
      }
      if (!step) step = 'checkin';
      if (step === 'checkout' && !_dates.checkin) step = 'checkin';

      setCurrentTag(step);
      setIsPopoverOpen(true);
      popover.open({
        tag: step,
        checkin: _dates.checkin,
        checkout: _dates.checkout,
        onSelectDate,
      });
    },
    [dates, onSelectDate, popover, tag]
  );

  useEffect(() => {
    if (
      popover.isOpened() &&
      ((isMobile && popover.props?.renderMethod === 'reference') ||
        (!isMobile && popover.props?.renderMethod === 'fullscreen'))
    ) {
      popover.close();
      setTimeout(() => {
        if (tag) openWithTag(tag);
      }, 100);
    }
  }, [isMobile, openWithTag, popover, tag]);

  return (
    <div>
      <StyledDatePicker
        border={border}
        size={size}
        className={isPopoverOpen ? 'open' : ''}
        ref={setContainerRef}
      >
        {showCalendarIcon && (
          <BEIcon className="calendar-icon" name={'calendar'} size={'lg'} />
        )}
        <button onClick={() => openWithTag('checkin')} className="date-button">
          <DateField
            date={dates.checkin}
            selected={isPopoverOpen && tag === 'checkin'}
            size={datefieldSize}
            placeholder={checkinPlaceholder}
          />
        </button>
        <BEIcon
          className="arrow-icon"
          name={'arrow-right'}
          size={'md'}
          weight={'solid'}
        />
        <button onClick={() => openWithTag('checkout')} className="date-button">
          <DateField
            date={dates.checkout}
            selected={isPopoverOpen && tag === 'checkout'}
            size={datefieldSize}
            placeholder={checkoutPlaceholder}
          />
        </button>
      </StyledDatePicker>
    </div>
  );
};

export default DatePicker;
